/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import {
  Goal,
  Lesson,
  LessonType,
  Topic,
  UserReflection,
  UserTest,
} from "@ictlearningsciences/pal3-core/types";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  deleteUserTest,
  saveUserReflection,
  saveUserTest,
  saveUserTests,
} from "../store/slices/userData";
import { useWithSurveys, Validation } from "./useWithSurveys";
import { useWithDialogue } from "./useWithDialogue";
import { getCompletedLessons, getLessonsForGoal } from "../helpers";
import { DialogueMessage } from "@ictlearningsciences/pal3-core/types";
import { useWithUserStudy } from "./useWithUserStudy";

interface UseWithUserTest {
  asvabEnabled: boolean;
  reflection: UserReflection | undefined;
  refValidation: Validation | undefined;
  refNeeded: DialogueMessage | undefined;
  getReflection: (lesson: Lesson) => UserReflection;
  checkReflection: () => void;
  editReflection: (notes: string) => void;
  saveReflection: () => void;
  saveTest: (test: UserTest) => void;
  saveTests: (tests: UserTest[]) => void;
  deleteTest: (test: UserTest) => void;
}

export function useWithUserTest(lesson?: Lesson): UseWithUserTest {
  const dispatch = useAppDispatch();
  const { lessons } = useAppSelector((state) => state.data);
  const { user, records, userReflections } = useAppSelector(
    (state) => state.userData
  );
  const { inStudy } = useWithUserStudy();

  const [asvabEnabled] = React.useState<boolean>(inStudy("ASVABPrepL2L"));
  const [reflection, setReflection] = React.useState<UserReflection>();
  const [refValidation, setRefValidation] = React.useState<Validation>();
  const [refNeeded, setRefNeeded] = React.useState<DialogueMessage>();

  const { validateText } = useWithSurveys();
  const { playMessage } = useWithDialogue();

  React.useEffect(() => {
    checkReflection();
  }, [lesson, userReflections, records]);

  function checkReflection(): void {
    if (!lesson) return;
    const r = getReflection(lesson);
    const val = validateText(r.notes, lesson.reflectionValidation);
    setReflection(r);
    setRefNeeded(undefined);
    if (r.notes && val.valid) return;

    // if ISRL mode is enabled
    if (asvabEnabled) {
      const { completedLessons } = getCompletedLessons(records, lessons);
      // passive lessons need self-reflections
      if (lesson.type === LessonType.PASSIVE) {
        let numPassive = 0,
          totalRefs = 0,
          past5 = 0,
          past10 = 0;
        for (const l of completedLessons) {
          const ref = getReflection(l);
          if (ref) {
            totalRefs++;
          }
          if (l.type === LessonType.PASSIVE) {
            if (ref) {
              if (numPassive < 5) past5++;
              if (numPassive < 10) past10++;
            }
            numPassive++;
          }
        }
        // must complete at least 5 distinct lesson self-reflections
        if (totalRefs < 5) {
          setRefNeeded({
            text: "Taking notes will help you retain and improve your learning. Let's take some now before moving on.",
          });
        }
        // after 20 distinct self-reflections, track average number of notes over the last 10 passive lessons
        // if less than 2 done, prompt again
        else if (totalRefs > 20 && past10 < 2) {
          setRefNeeded({
            text: "Looks like you haven't taken any notes in a while. Note-taking done properly will help you improve your knowledge.",
          });
        }
        // after 10 distinct self-reflections, track average number of notes over the last 5 passive lessons
        // if less than 2 done, prompt again
        else if (totalRefs > 10 && past5 < 2) {
          setRefNeeded({
            text: "Looks like you haven't taken any notes in a while. Note-taking done properly will help you improve your knowledge.",
          });
        }
      }
    }
  }

  function getReflection(lesson: Lesson): UserReflection {
    return (
      userReflections.find((u) => u.lesson === lesson.id) || {
        id: "",
        user: user?.id || "",
        lesson: lesson.id,
        notes: "",
      }
    );
  }

  function editReflection(notes: string): void {
    if (!reflection || !lesson) return;
    const val = validateText(notes, lesson.reflectionValidation);
    setReflection({
      ...reflection,
      notes,
    });
    setRefValidation(val);
  }

  function saveReflection(): void {
    if (!reflection || !lesson) return;
    if (refValidation && !refValidation.valid) {
      playMessage(refValidation.message);
    } else {
      dispatch(saveUserReflection({ lesson, notes: reflection.notes }));
    }
  }

  function saveTest(test: UserTest): void {
    dispatch(saveUserTest(test));
  }

  function saveTests(tests: UserTest[]): void {
    dispatch(saveUserTests(tests));
  }

  function deleteTest(test: UserTest): void {
    dispatch(deleteUserTest(test));
  }

  return {
    asvabEnabled,
    // user reflection
    reflection,
    refValidation,
    refNeeded,
    getReflection,
    checkReflection,
    editReflection,
    saveReflection,
    // user tests
    saveTest,
    saveTests,
    deleteTest,
  };
}

export function getUserTestQuestions(
  userTests: UserTest[],
  props: {
    lesson?: Lesson;
    topic?: Topic;
    goal?: Goal;
    num?: number;
  }
): UserTest[] {
  const { lesson, topic, goal, num } = props;
  let results = userTests;
  if (lesson) {
    results = userTests.filter((t) => t.lesson === lesson.id);
  } else if (topic) {
    const lessons = topic.lessons.map((l) => l.id);
    results = userTests.filter((t) => lessons.includes(t.lesson));
  } else if (goal) {
    const lessons = getLessonsForGoal(goal).map((l) => l.id);
    results = userTests.filter((t) => lessons.includes(t.lesson));
  }
  if (num && results.length > num) {
    const shuffled = results.sort(() => 0.5 - Math.random());
    results = shuffled.slice(0, num);
  }
  return results;
}
